import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/internal/operators';

export interface MetalPrice {
	metal: string;
	price: number;
	unit: string;
	currency: string;
}

export interface MetalsResponse {
	success?: boolean;
	status?: string;
	error_code?: number;
	error_message?: string;
	data?: {
		gold?: number;
		silver?: number;
		platinum?: number;
		palladium?: number;
	};
	timestamp?: string;
	error?: string;
	// Actual API response structure
	metals?: {
		gold?: number;
		silver?: number;
		platinum?: number;
		palladium?: number;
		[key: string]: number | undefined;
	};
	currency?: string;
	unit?: string;
	currencies?: { [key: string]: number };
	timestamps?: {
		metal?: string;
		currency?: string;
	};
}

@Injectable({
	providedIn: 'root'
})
export class MetalsPricingService {
	private readonly apiUrl = 'https://api.metals.dev/v1/latest';
	private readonly apiKey = 'H6MOQCNPIEWOZFWOQIWK742WOQIWK';
	private readonly defaultCurrency = 'USD';
	private readonly defaultUnit = 'toz';

	constructor(private http: HttpClient) {}

	public httpOptions = {
		headers: new HttpHeaders({
			'Content-Type': 'application/json',
		})
	};

	/**
	 * Fetch current metal prices from static JSON file
	 * @returns Observable with metal prices data
	 */
	getMetalPrices(): Observable<MetalsResponse> {
		const jsonFileUrl = 'assets/data/metals-pricing-response.json';
		
		return this.http.get<MetalsResponse>(jsonFileUrl, this.httpOptions).pipe(
			map((response: MetalsResponse) => {
				// Handle API error responses that come as 200 OK but with error in body
				if (response.status === 'failure' || response.error_code) {
					throw {
						success: false,
						status: response.status,
						error_code: response.error_code,
						error_message: response.error_message || 'API error occurred',
						data: {}
					};
				}
				// Map actual API response structure to expected format
				// API returns: { status: "success", metals: {...}, timestamps: {...} }
				// We need: { success: true, data: {...}, timestamp: "..." }
				const metalsData = response.metals || response.data || {};
				const metalTimestamp = response.timestamps?.metal || response.timestamp || new Date().toISOString();
				
				// Normalize successful response - include all metals
				return {
					success: response.status === 'success' || response.success === true,
					data: metalsData, // Include all metals, not just the 4 main ones
					timestamp: metalTimestamp,
					// Preserve original response data for potential future use
					metals: response.metals,
					currency: response.currency || this.defaultCurrency,
					unit: response.unit || this.defaultUnit,
					timestamps: response.timestamps
				} as MetalsResponse;
			}),
			catchError(this.handleError)
		);
	}

	/**
	 * Format metal prices into a structured array
	 * @param response API response
	 * @returns Array of MetalPrice objects
	 */
	formatMetalPrices(response: MetalsResponse): MetalPrice[] {
		if (!response || !response.data) {
			return [];
		}

		const prices: MetalPrice[] = [];
		// Metal name mappings for display
		const metalNameMap: { [key: string]: string } = {
			'gold': 'Gold',
			'silver': 'Silver',
			'platinum': 'Platinum',
			'palladium': 'Palladium',
			'lbma_gold_am': 'LBMA Gold AM',
			'lbma_gold_pm': 'LBMA Gold PM',
			'lbma_silver': 'LBMA Silver',
			'lbma_platinum_am': 'LBMA Platinum AM',
			'lbma_platinum_pm': 'LBMA Platinum PM',
			'lbma_palladium_am': 'LBMA Palladium AM',
			'lbma_palladium_pm': 'LBMA Palladium PM',
			'mcx_gold': 'MCX Gold',
			'mcx_gold_am': 'MCX Gold AM',
			'mcx_gold_pm': 'MCX Gold PM',
			'mcx_silver': 'MCX Silver',
			'mcx_silver_am': 'MCX Silver AM',
			'mcx_silver_pm': 'MCX Silver PM',
			'ibja_gold': 'IBJA Gold',
			'copper': 'Copper',
			'aluminum': 'Aluminum',
			'lead': 'Lead',
			'nickel': 'Nickel',
			'zinc': 'Zinc',
			'lme_copper': 'LME Copper',
			'lme_aluminum': 'LME Aluminum',
			'lme_lead': 'LME Lead',
			'lme_nickel': 'LME Nickel',
			'lme_zinc': 'LME Zinc'
		};

		// Get all metals from response (either from data or metals property)
		const metalsData = response.metals || response.data || {};
		
		// Process all available metals
		Object.keys(metalsData).forEach(key => {
			const price = metalsData[key];
			if (price !== undefined && price !== null && typeof price === 'number') {
				const displayName = metalNameMap[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
				prices.push({
					metal: displayName,
					price: price,
					unit: this.defaultUnit,
					currency: this.defaultCurrency
				});
			}
		});

		// Sort by metal name for consistent display
		return prices.sort((a, b) => a.metal.localeCompare(b.metal));
	}

	/**
	 * Handle HTTP errors
	 */
	private handleError(error: any): Observable<never> {
		let errorMessage = 'Failed to load metal pricing data';
		
		// Handle API error responses (from map operator)
		if (error.error_message) {
			errorMessage = error.error_message;
		} else if (error.error instanceof ErrorEvent) {
			// Client-side error (network, file not found, etc.)
			errorMessage = 'Error: Unable to load metal pricing data file';
		} else if (error.error) {
			// HTTP error response
			if (error.error.error_message) {
				errorMessage = error.error.error_message;
			} else if (error.status === 0) {
				errorMessage = 'Error: Unable to load metal pricing data file';
			} else if (error.status === 404) {
				errorMessage = 'Error: Metal pricing data file not found';
			} else if (error.status >= 500) {
				errorMessage = 'Error: Server error while loading metal pricing data';
			} else {
				errorMessage = `Error ${error.status}: ${error.message || 'Unable to load metal pricing data'}`;
			}
		}

		return throwError(() => ({
			success: false,
			error: errorMessage,
			data: {}
		}));
	}
}



